import React, { useState, useEffect, useCallback } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { 
  ChartBarIcon, 
  ClockIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarIcon,
  FunnelIcon,
  BoltIcon,
  ChevronDownIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import getModerationAnalytics from '../api/moderation/getAnalytics';
import getModerationStats from '../api/moderation/getModerationStats';
import getCategories from '../api/getCategories';

const ModeratorAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [categories, setCategories] = useState([]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = { timeRange };
      if (timeRange === 'custom' && startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      if (categoryId) {
        params.categoryId = categoryId;
      }

      const [analyticsData, statsData] = await Promise.all([
        getModerationAnalytics(params),
        getModerationStats()
      ]);
      
      setAnalytics(analyticsData);
      setStats(statsData);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [timeRange, startDate, endDate, categoryId]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    getCategories().then(setCategories).catch(console.error);
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return num.toLocaleString('ru-RU');
  };

  const formatModerationTime = (value) => {
    if (!value || value <= 0) return null;
    // Бэк иногда шлёт секунды/мс вместо минут — автодетекция единиц
    let minutes = value;
    if (value > 1000) {
      const candidates = [
        { minutes: value, type: 'minutes' },
        { minutes: value / 60, type: 'seconds' },
        { minutes: value / 60000, type: 'milliseconds' }
      ];
      // Выбираем вариант, дающий разумное время модерации [0.01, 120] минут,
      // приоритет тому что ближе к 5 минутам (типичное время)
      const reasonable = candidates.filter(c => c.minutes >= 0.01 && c.minutes <= 120);
      if (reasonable.length > 0) {
        minutes = reasonable.reduce((best, curr) =>
          Math.abs(curr.minutes - 5) < Math.abs(best.minutes - 5) ? curr : best
        ).minutes;
      } else {
        minutes = value / 60000;
      }
    }
    if (minutes < 1) {
      return { label: `${minutes.toFixed(1)} мин`, minutes };
    }
    if (minutes < 60) {
      return { label: `${Math.round(minutes)} мин`, minutes };
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    if (hours < 24) {
      return { label: `${hours}ч ${mins}мин`, minutes };
    }
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return { label: `${days}д ${remainingHours}ч`, minutes };
  };

  const getPercentageChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    return change.toFixed(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Загрузка аналитики...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg">
        <p className="font-medium">Ошибка загрузки аналитики</p>
        <p className="text-sm">{error}</p>
        <button
          onClick={fetchAnalytics}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Попробовать снова
        </button>
      </div>
    );
  }

  const data = analytics ? {
    ...analytics,
    overview: {
      ...analytics.overview,
      daily_moderation_goal: analytics.overview?.daily_moderation_goal ?? stats?.daily_moderation_goal ?? 50,
      oldest_pending_age: analytics.overview?.oldest_pending_age ?? stats?.oldest_pending_age ?? '—',
      moderation_time_status: analytics.overview?.moderation_time_status ?? stats?.moderation_time_status ?? null,
    }
  } : {
    overview: {
      total_announcements: stats?.total || 0,
      pending: stats?.pending || 0,
      approved: stats?.approved || 0,
      rejected: stats?.rejected || 0,
      approval_rate: stats?.approval_rate || null,
      average_moderation_time: 0,
      moderation_today: 0,
      moderation_yesterday: 0,
      daily_moderation_goal: stats?.daily_moderation_goal || 50,
      oldest_pending_age: stats?.oldest_pending_age || '—',
      moderation_time_status: stats?.moderation_time_status || null
    },
    trends: [],
    by_category: [],
    by_status: {
      pending: stats?.pending || 0,
      approved: stats?.approved || 0,
      rejected: stats?.rejected || 0
    },
    daily_stats: []
  };
  const percentageChange = getPercentageChange(data.overview.moderation_today, data.overview.moderation_yesterday);

  return (
    <div className="space-y-6">
      <div className="p-6 rounded-lg shadow-lg border border-border-light dark:border-white/10 space-y-6 bg-white dark:bg-[#1C2127]">
        {/* Header with time range selector */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Аналитика модерации</h2>
            <p className="text-gray-500 dark:text-gray-400">Статистика и метрики модерации объявлений</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Time range toggles */}
            <div className="flex items-center gap-2">
              {[
                { label: 'Сегодня', value: 'today' },
                { label: '7 дней', value: '7d' },
                { label: '30 дней', value: '30d' },
                { label: '90 дней', value: '90d' },
                { label: 'Произвольная дата', value: 'custom' },
              ].map((item) => {
                const isActive = timeRange === item.value;
                return (
                  <button
                    key={item.value}
                    onClick={() => {
                      setTimeRange(item.value);
                      if (item.value !== 'custom') {
                        setStartDate('');
                        setEndDate('');
                      }
                    }}
                    className={`relative px-3 py-2 rounded-lg text-[14px] font-medium transition-all flex items-center justify-center ${
                      isActive
                        ? 'border border-[#4F45E5]'
                        : 'border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                    style={
                      isActive
                        ? {
                            background: 'rgba(79, 69, 229, 0.1)',
                          }
                        : undefined
                    }
                  >
                    <span
                      className={`${isActive ? 'bg-clip-text text-transparent font-semibold' : ''}`}
                      style={
                        isActive
                          ? {
                              backgroundImage: 'linear-gradient(135deg, #4F45E5 0%, #7C3AED 100%)',
                            }
                          : undefined
                      }
                    >
                      {item.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {timeRange === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="dark-input text-gray-900 dark:text-white bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm h-[38px]"
                  placeholder="От"
                />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="dark-input text-gray-900 dark:text-white bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm h-[38px]"
                  placeholder="До"
                />
              </div>
            )}

            {/* Category dropdown */}
            <div className="relative">
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="appearance-none text-gray-900 dark:text-white bg-white dark:bg-[#222630] border border-gray-300 dark:border-[#333333] rounded-lg px-4 pr-8 text-[14px] cursor-pointer py-2 leading-normal focus:outline-none focus:ring-2 focus:ring-[#4F45E5] focus:border-[#4F45E5] hover:border-gray-400 dark:hover:border-gray-500"
              >
                <option value="" className="bg-white dark:bg-gray-700">Все категории</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id} className="bg-white dark:bg-gray-700">
                    {cat.name_kz || cat.name_ru || cat.name}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500" />
            </div>

            {/* Refresh button */}
            <button
              onClick={fetchAnalytics}
              className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Обновить"
            >
              <ArrowPathIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white dark:bg-[#222630] rounded-lg p-5 border border-border-light dark:border-white/10 flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[14px] text-gray-500 dark:text-gray-400 leading-5">Модераций сегодня</p>
              <div className="flex items-center gap-2 mt-[5px]">
                <p className="text-[24px] font-bold text-gray-900 dark:text-white">{formatNumber(data.overview.moderation_today)}</p>
                <p className="text-xs text-gray-500">Вчера: {formatNumber(data.overview.moderation_yesterday)}</p>
                {percentageChange !== null && (
                  <div className={`flex items-center text-sm ${percentageChange > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {percentageChange > 0 ? (
                      <ArrowTrendingUpIcon className="w-4 h-4 mr-1" />
                    ) : (
                      <ArrowTrendingDownIcon className="w-4 h-4 mr-1" />
                    )}
                    {Math.abs(percentageChange)}%
                  </div>
                )}
              </div>
            </div>
            <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 relative">
              <div
                className="absolute inset-0 rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, #3B82F6 0%, #9333EA 100%)',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                  padding: '1px',
                }}
              />
              <div
                className="absolute inset-0 rounded-lg opacity-10"
                style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #9333EA 100%)' }}
              />
              <ChartBarIcon
                className="w-6 h-6 relative z-10"
                style={{ stroke: 'url(#card1_grad)', strokeWidth: 2 }}
              />
            </div>
          </div>
          <div className="mt-auto pt-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">Цель дня: {data.overview.daily_moderation_goal || 50}</span>
              <span className="text-xs font-medium text-gray-900 dark:text-white">
                {Math.min(100, Math.round((data.overview.moderation_today / (data.overview.daily_moderation_goal || 50)) * 100))}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="h-2 rounded-full"
                style={{
                  width: `${Math.min(100, (data.overview.moderation_today / (data.overview.daily_moderation_goal || 50)) * 100)}%`,
                  background: 'linear-gradient(90deg, #3B82F6, #9333EA)'
                }}
              />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-[#222630] rounded-lg p-5 border border-border-light dark:border-white/10 flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[14px] text-gray-500 dark:text-gray-400 leading-5">В ожидании</p>
              <div className="flex items-center gap-2 mt-[5px]">
                <p className="text-[24px] font-bold text-gray-900 dark:text-white">{formatNumber(data.overview.pending)}</p>
                <p className="text-xs text-gray-500">
                  {data.overview.total_announcements > 0
                    ? `${((data.overview.pending / data.overview.total_announcements) * 100).toFixed(1)}%`
                    : '0%'}
                </p>
              </div>
              <div className="flex items-center gap-1 mt-2">
                <ClockIcon className="w-4 h-4 relative z-10" style={{ stroke: 'url(#card2_grad)', strokeWidth: 2 }} />
                <span className="text-xs font-medium bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(90deg, #EAB308, #EA580C)' }}>
                  Старшее в очереди: {data.overview.oldest_pending_age || '—'}
                </span>
              </div>
            </div>
            <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 relative">
              <div
                className="absolute inset-0 rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, #EAB308 0%, #EA580C 100%)',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                  padding: '1px',
                }}
              />
              <div
                className="absolute inset-0 rounded-lg opacity-10"
                style={{ background: 'linear-gradient(135deg, #EAB308 0%, #EA580C 100%)' }}
              />
              <ClockIcon
                className="w-6 h-6 relative z-10"
                style={{ stroke: 'url(#card2_grad)', strokeWidth: 2 }}
              />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-[#222630] rounded-lg p-5 border border-border-light dark:border-white/10 flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[14px] text-gray-500 dark:text-gray-400 leading-5">Одобрено</p>
              <p className="text-[24px] font-bold text-gray-900 dark:text-white mt-[5px]">{formatNumber(data.overview.approved)}</p>
            </div>
            <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 relative">
              <div
                className="absolute inset-0 rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, #22C55E 0%, #059669 100%)',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                  padding: '1px',
                }}
              />
              <div
                className="absolute inset-0 rounded-lg opacity-10"
                style={{ background: 'linear-gradient(135deg, #22C55E 0%, #059669 100%)' }}
              />
              <CheckCircleIcon
                className="w-6 h-6 relative z-10"
                style={{ stroke: 'url(#card3_grad)', strokeWidth: 2 }}
              />
            </div>
          </div>
          <div className="mt-auto pt-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">Процент одобрения</span>
              <span className="text-xs font-medium text-gray-900 dark:text-white">
                {(data.overview.approval_rate ?? 0).toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="h-2 rounded-full"
                style={{
                  width: `${Math.min(100, data.overview.approval_rate ?? 0)}%`,
                  background: 'linear-gradient(90deg, #22C55E, #059669)'
                }}
              />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-[#222630] rounded-lg p-5 border border-border-light dark:border-white/10 flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-[14px] text-gray-500 dark:text-gray-400 leading-5">Среднее время модерации</p>
              {(() => {
                const time = formatModerationTime(data.overview.average_moderation_time);
                if (!time) {
                  return (
                    <>
                      <p className="text-[24px] font-bold text-gray-900 dark:text-white mt-[5px]">—</p>
                      <div className="flex items-center gap-1 mt-2">
                        <BoltIcon className="w-4 h-4 relative z-10" style={{ stroke: '#9CA3AF', strokeWidth: 2 }} />
                        <span className="text-xs font-medium text-gray-400 dark:text-gray-500">Нет данных</span>
                      </div>
                    </>
                  );
                }
                const status = data.overview.moderation_time_status;
                const label = status?.label || '—';
                const gradId = status?.color === 'green' ? 'status_green'
                  : status?.color === 'yellow' ? 'status_yellow'
                  : status?.color === 'red' ? 'status_red'
                  : null;
                const textGradient = status?.color === 'green' ? 'linear-gradient(90deg, #22C55E, #059669)'
                  : status?.color === 'yellow' ? 'linear-gradient(90deg, #EAB308, #EA580C)'
                  : status?.color === 'red' ? 'linear-gradient(90deg, #EF4444, #DC2626)'
                  : 'linear-gradient(90deg, #9CA3AF, #6B7280)';
                return (
                  <>
                    <p className="text-[24px] font-bold text-gray-900 dark:text-white mt-[5px]">{time.label}</p>
                    <div className="flex items-center gap-1 mt-2">
                      <BoltIcon className="w-4 h-4 relative z-10" style={{ stroke: gradId ? `url(#${gradId})` : '#9CA3AF', strokeWidth: 2 }} />
                      <span className="text-xs font-medium bg-clip-text text-transparent" style={{ backgroundImage: textGradient }}>
                        {label}
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 relative">
              <div
                className="absolute inset-0 rounded-lg"
                style={{
                  background: 'linear-gradient(135deg, #C084FC 0%, #7B25EB 100%)',
                  WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                  WebkitMaskComposite: 'xor',
                  maskComposite: 'exclude',
                  padding: '1px',
                }}
              />
              <div
                className="absolute inset-0 rounded-lg opacity-10"
                style={{ background: 'linear-gradient(135deg, #A855F7 0%, #DB2777 100%)' }}
              />
              <ClockIcon
                className="w-6 h-6 relative z-10"
                style={{ stroke: 'url(#card4_grad)', strokeWidth: 2 }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Statistics */}
        <div className="bg-white dark:bg-[#222630] rounded-lg p-6 border border-border-light dark:border-white/10">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
              <CalendarIcon className="w-5 h-5 mr-2" />
              Статистика по дням
            </h3>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-[#22C55E]" />
                <span className="text-gray-500 dark:text-gray-400">Одобрено</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-[#EF4444]" />
                <span className="text-gray-500 dark:text-gray-400">Отклонено</span>
              </div>
            </div>
          </div>

          {(() => {
            const toIso = (d) => d.toISOString().split('T')[0];
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            let start;
            let end = new Date(today);
            switch (timeRange) {
              case 'today':
                start = new Date(today);
                break;
              case '7d':
                start = new Date(today);
                start.setDate(start.getDate() - 6);
                break;
              case '30d':
                start = new Date(today);
                start.setDate(start.getDate() - 29);
                break;
              case '90d':
                start = new Date(today);
                start.setDate(start.getDate() - 89);
                break;
              case 'custom':
                start = startDate ? new Date(startDate) : new Date(today);
                end = endDate ? new Date(endDate) : new Date(today);
                start.setHours(0, 0, 0, 0);
                end.setHours(0, 0, 0, 0);
                break;
              default:
                start = new Date(today);
                start.setDate(start.getDate() - 6);
            }

            const days = [];
            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
              days.push(toIso(new Date(d)));
            }

            const apiMap = new Map();
            (data.daily_stats || []).forEach((item) => {
              if (item.date) {
                apiMap.set(item.date, {
                  approved: item.approved || 0,
                  rejected: item.rejected || 0,
                  total: item.total || 0,
                });
              }
            });

            let chartData = days.map((date) => {
              const api = apiMap.get(date);
              const d = new Date(date);
              return {
                date,
                label: `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')}`,
                Одобрено: api ? api.approved : 0,
                Отклонено: api ? api.rejected : 0,
                Всего: api ? api.total : 0,
              };
            });

            // Fallback для "Сегодня": если daily_stats пустой, но есть moderation_today
            if (timeRange === 'today' && chartData.every((d) => d.Всего === 0) && (data.overview?.moderation_today > 0 || data.overview?.moderation_today === 0)) {
              chartData = [{
                date: toIso(today),
                label: `${today.getDate()}.${String(today.getMonth() + 1).padStart(2, '0')}`,
                Одобрено: data.overview.moderation_today,
                Отклонено: 0,
                Всего: data.overview.moderation_today,
              }];
            }

            const hasData = chartData.some((d) => d.Всего > 0);
            const totalDays = days.length;

            return hasData ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.2)" vertical={false} />
                    <XAxis
                      dataKey="label"
                      stroke="#9CA3AF"
                      tick={{ fill: '#9CA3AF', fontSize: totalDays > 20 ? 10 : 12 }}
                      axisLine={{ stroke: 'rgba(156,163,175,0.3)' }}
                      tickLine={false}
                    />
                    <YAxis
                      stroke="#9CA3AF"
                      tick={{ fill: '#9CA3AF', fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1C2127',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        fontSize: '13px',
                      }}
                      labelStyle={{ color: '#9CA3AF' }}
                      itemStyle={{ color: '#fff' }}
                      formatter={(value, name) => [formatNumber(value), name]}
                      labelFormatter={(label) => `Дата: ${label}`}
                    />
                    <Bar dataKey="Одобрено" stackId="a" fill="#22C55E" radius={[0, 0, 4, 4]} />
                    <Bar dataKey="Отклонено" stackId="a" fill="#EF4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-48 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                <ChartBarIcon className="w-12 h-12 mb-3 opacity-30" />
                <p className="text-sm">Нет данных за выбранный период</p>
              </div>
            );
          })()}
        </div>

        {/* Status Distribution */}
        <div className="bg-white dark:bg-[#222630] rounded-lg p-6 border border-border-light dark:border-white/10">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
            <FunnelIcon className="w-5 h-5 mr-2" />
            Распределение по статусам
          </h3>
          {(() => {
            const total = data.overview.total_announcements || 0;
            const chartData = [
              { name: 'В ожидании', value: data.by_status.pending || 0, color: '#EAB308' },
              { name: 'Одобрено', value: data.by_status.approved || 0, color: '#22C55E' },
              { name: 'Отклонено', value: data.by_status.rejected || 0, color: '#EF4444' },
            ];
            return (
              <div className="flex items-center gap-6">
                <div className="flex-1 space-y-4">
                  {chartData.map((item) => (
                    <div key={item.name} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-4 h-4 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-gray-600 dark:text-gray-300">{item.name}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-gray-900 dark:text-white font-medium">{formatNumber(item.value)}</span>
                        {total > 0 && (
                          <span className="text-gray-500 dark:text-gray-400 text-xs ml-2">
                            ({((item.value / total) * 100).toFixed(1)}%)
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="w-40 h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="value"
                        stroke="none"
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Trends Chart */}
      {(() => {
        const toIso = (d) => d.toISOString().split('T')[0];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let start;
        let end = new Date(today);
        switch (timeRange) {
          case 'today':
            start = new Date(today);
            break;
          case '7d':
            start = new Date(today);
            start.setDate(start.getDate() - 6);
            break;
          case '30d':
            start = new Date(today);
            start.setDate(start.getDate() - 29);
            break;
          case '90d':
            start = new Date(today);
            start.setDate(start.getDate() - 89);
            break;
          case 'custom':
            start = startDate ? new Date(startDate) : new Date(today);
            end = endDate ? new Date(endDate) : new Date(today);
            start.setHours(0, 0, 0, 0);
            end.setHours(0, 0, 0, 0);
            break;
          default:
            start = new Date(today);
            start.setDate(start.getDate() - 6);
        }

        const days = [];
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          days.push(toIso(new Date(d)));
        }

        const apiMap = new Map();
        (data.daily_stats || []).forEach((item) => {
          if (item.date) apiMap.set(item.date, item.total || ((item.approved || 0) + (item.rejected || 0)));
        });
        (data.trends || []).forEach((item) => {
          if (item.date && !apiMap.has(item.date)) apiMap.set(item.date, item.count || 0);
        });

        let chartData = days.map((date) => ({
          date,
          count: apiMap.get(date) || 0,
        }));

        // Если для "Сегодня" API не вернул daily_stats, но есть moderation_today в overview
        if (timeRange === 'today' && chartData.every((d) => d.count === 0) && (data.overview?.moderation_today > 0 || data.overview?.moderation_today === 0)) {
          chartData = [{
            date: toIso(today),
            count: data.overview.moderation_today,
          }];
        }

        const totalAvailable = chartData.length;
        const hasData = chartData.some((d) => d.count > 0);

        return (
          <div className="bg-white dark:bg-[#222630] rounded-lg p-6 border border-border-light dark:border-white/10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
                  <ChartBarIcon className="w-5 h-5 mr-2" />
                  Динамика модерации
                </h3>
                {totalAvailable > 0 && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                    {totalAvailable} {totalAvailable === 1 ? 'день' : totalAvailable < 5 ? 'дня' : 'дней'}
                  </span>
                )}
              </div>
              {hasData && (
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-[#4F45E5]" />
                    <span className="text-gray-500 dark:text-gray-400">Модераций</span>
                  </div>
                </div>
              )}
            </div>

            {hasData ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="trendArea" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#4F45E5" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#4F45E5" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.2)" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(d) => {
                        const date = new Date(d);
                        return `${date.getDate()}.${String(date.getMonth() + 1).padStart(2, '0')}`;
                      }}
                      stroke="#9CA3AF"
                      tick={{ fill: '#9CA3AF', fontSize: 12 }}
                      axisLine={{ stroke: 'rgba(156,163,175,0.3)' }}
                      tickLine={false}
                    />
                    <YAxis
                      stroke="#9CA3AF"
                      tick={{ fill: '#9CA3AF', fontSize: 12 }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1C2127',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        fontSize: '13px',
                      }}
                      labelStyle={{ color: '#9CA3AF' }}
                      itemStyle={{ color: '#fff' }}
                      formatter={(value) => [formatNumber(value), 'Модераций']}
                      labelFormatter={(label) => formatDate(label)}
                    />
                    <Area
                      type="monotone"
                      dataKey="count"
                      stroke="#4F45E5"
                      strokeWidth={2.5}
                      fill="url(#trendArea)"
                      dot={{ r: 4, fill: '#4F45E5', stroke: '#fff', strokeWidth: 2 }}
                      activeDot={{ r: 6, fill: '#4F45E5', stroke: '#fff', strokeWidth: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-48 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                <ChartBarIcon className="w-12 h-12 mb-3 opacity-30" />
                <p className="text-sm">Нет данных за выбранный период</p>
              </div>
            )}
          </div>
        );
      })()}

      {/* Category Statistics */}
      {data.by_category && data.by_category.length > 0 && (
        <div className="bg-white dark:bg-[#222630] rounded-lg p-6 border border-border-light dark:border-white/10">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Статистика по категориям</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Категория</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ожидает</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Одобрено</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Отклонено</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Всего</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Распределение</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {[...data.by_category]
                  .sort((a, b) => (b.total || 0) - (a.total || 0))
                  .map((category, index) => {
                    const total = category.total || 0;
                    const pending = category.pending || 0;
                    const approved = category.approved || 0;
                    const rejected = category.rejected || 0;
                    const pendingPct = total > 0 ? ((pending / total) * 100).toFixed(0) : 0;
                    const approvedPct = total > 0 ? ((approved / total) * 100).toFixed(0) : 0;
                    const rejectedPct = total > 0 ? ((rejected / total) * 100).toFixed(0) : 0;
                    return (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-4 py-3 text-gray-900 dark:text-white font-medium whitespace-nowrap">{category.name || 'Без категории'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-yellow-600 dark:text-yellow-400 font-medium">{formatNumber(pending)}</span>
                          {total > 0 && <span className="text-[11px] text-gray-400 dark:text-gray-500 ml-1">({pendingPct}%)</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-green-600 dark:text-green-400 font-medium">{formatNumber(approved)}</span>
                          {total > 0 && <span className="text-[11px] text-gray-400 dark:text-gray-500 ml-1">({approvedPct}%)</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-red-600 dark:text-red-400 font-medium">{formatNumber(rejected)}</span>
                          {total > 0 && <span className="text-[11px] text-gray-400 dark:text-gray-500 ml-1">({rejectedPct}%)</span>}
                        </td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white font-medium whitespace-nowrap">{formatNumber(total)}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="w-24 h-2 rounded-full overflow-hidden flex bg-gray-200 dark:bg-gray-700">
                            {total > 0 && (
                              <>
                                <div style={{ width: `${pendingPct}%` }} className="h-full bg-yellow-400" />
                                <div style={{ width: `${approvedPct}%` }} className="h-full bg-green-400" />
                                <div style={{ width: `${rejectedPct}%` }} className="h-full bg-red-400" />
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </div>

      <svg width="0" height="0" className="absolute">
        <defs>
          <linearGradient id="card1_grad" x1="0" y1="12" x2="24" y2="12" gradientUnits="userSpaceOnUse">
            <stop stopColor="#3B82F6" />
            <stop offset="1" stopColor="#9333EA" />
          </linearGradient>
          <linearGradient id="card2_grad" x1="0" y1="12" x2="24" y2="12" gradientUnits="userSpaceOnUse">
            <stop stopColor="#EAB308" />
            <stop offset="1" stopColor="#EA580C" />
          </linearGradient>
          <linearGradient id="card3_grad" x1="0" y1="12" x2="24" y2="12" gradientUnits="userSpaceOnUse">
            <stop stopColor="#22C55E" />
            <stop offset="1" stopColor="#059669" />
          </linearGradient>
          <linearGradient id="card4_grad" x1="0" y1="12" x2="24" y2="12" gradientUnits="userSpaceOnUse">
            <stop stopColor="#C084FC" />
            <stop offset="1" stopColor="#7B25EB" />
          </linearGradient>
          <linearGradient id="status_green" x1="0" y1="12" x2="24" y2="12" gradientUnits="userSpaceOnUse">
            <stop stopColor="#22C55E" />
            <stop offset="1" stopColor="#059669" />
          </linearGradient>
          <linearGradient id="status_yellow" x1="0" y1="12" x2="24" y2="12" gradientUnits="userSpaceOnUse">
            <stop stopColor="#EAB308" />
            <stop offset="1" stopColor="#EA580C" />
          </linearGradient>
          <linearGradient id="status_red" x1="0" y1="12" x2="24" y2="12" gradientUnits="userSpaceOnUse">
            <stop stopColor="#EF4444" />
            <stop offset="1" stopColor="#DC2626" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

export default ModeratorAnalytics;

